import type { ImageItem } from "../../vendor/src/features/remotion-editor/editor/items/image/image-item-type";
import type { VideoItem } from "../../vendor/src/features/remotion-editor/editor/items/video/video-item-type";
import type { PositionKeyframe } from "../../vendor/src/features/remotion-editor/editor/items/animation/position-keyframes";

export type SourceCard = { focusX: number; focusY: number; scale: number };
/** Encode the old cover/focus window as native crop data, keeping the visible rectangle unchanged. */
export function sourceCardToNativeCrop<T extends ImageItem | VideoItem>(
  item: T,
  media: { width: number; height: number },
  source: SourceCard,
): T {
  if (
    ![media.width, media.height, item.width, item.height, source.scale].every(
      (value) => Number.isFinite(value) && value > 0,
    ) ||
    source.scale < 1 ||
    ![source.focusX, source.focusY].every(
      (value) => Number.isFinite(value) && value >= 0 && value <= 100,
    )
  ) {
    throw new Error("원본 이미지 배치 수치를 확인하지 못했습니다.");
  }
  if (item.cropLeft || item.cropTop || item.cropRight || item.cropBottom)
    throw new Error("이미 직접 자른 이미지의 배치는 별도 대조가 필요합니다.");
  const scale =
    Math.max(item.width / media.width, item.height / media.height) *
    source.scale;
  const width = media.width * scale,
    height = media.height * scale;
  const excessX = width - item.width,
    excessY = height - item.height;
  const cropLeft = (excessX / width) * (source.focusX / 100),
    cropTop = (excessY / height) * (source.focusY / 100);
  return {
    ...item,
    width,
    height,
    left: item.left - cropLeft * width,
    top: item.top - cropTop * height,
    cropLeft,
    cropRight: Math.max(0, excessX / width - cropLeft),
    cropTop,
    cropBottom: Math.max(0, excessY / height - cropTop),
    keepAspectRatio: true,
  };
}

/** Frame-exact steps in the company's native keyframe model; no persistent legacy layout lock. */
export function sourcePositionSteps(
  base: { left: number; top: number },
  durationInFrames: number,
  changes: readonly { frame: number; left: number; top: number }[],
): PositionKeyframe[] {
  if (
    !Number.isInteger(durationInFrames) ||
    durationInFrames <= 0 ||
    ![base.left, base.top].every(Number.isFinite)
  )
    throw new Error("Invalid position domain");
  const samples = new Map<number, { left: number; top: number }>();
  for (const change of changes) {
    if (
      ![change.frame, change.left, change.top].every(Number.isFinite) ||
      !Number.isInteger(change.frame)
    )
      throw new Error("Invalid position sample");
    if (change.frame >= 0 && change.frame < durationInFrames)
      samples.set(change.frame, change);
  }
  if (
    [...samples.values()].every(
      (point) => point.left === base.left && point.top === base.top,
    )
  )
    return [];
  const result = new Map<number, PositionKeyframe>();
  let previous = { left: base.left, top: base.top };
  result.set(0, { frame: 0, dx: 0, dy: 0 });
  for (const [frame, point] of [...samples].sort(([a], [b]) => a - b)) {
    if (frame > 0)
      result.set(frame - 1, {
        frame: frame - 1,
        dx: previous.left - base.left,
        dy: previous.top - base.top,
      });
    result.set(frame, {
      frame,
      dx: point.left - base.left,
      dy: point.top - base.top,
    });
    previous = point;
  }
  return [...result.values()].sort((a, b) => a.frame - b.frame);
}
